import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { GalleryEditor } from "../components/GalleryEditor";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TransButtons } from "../components/TransButtons";
import { SECTOR_OPTIONS, STAGE_OPTIONS, EXIT_STATUS_OPTIONS } from "../lib/enums";

type TimelineItem = { year: string; event_zh?: string; event_en?: string };

type Project = {
  id: number;
  slug: string;
  sector: string;
  stage: "listed" | "pipeline" | "early";
  name_zh: string;
  name_en: string;
  ticker: string | null;
  market: string | null;
  market_en: string | null;
  ipo_date: string | null;
  kexi_invest_date: string | null;
  exit_status: string | null;
  return_multiple: string | number | null;
  summary_zh: string;
  summary_en: string;
  body_zh_md: string | null;
  body_en_md: string | null;
  logo_key: string | null;
  cover_key: string | null;
  gallery_keys: string[];
  timeline: TimelineItem[];
  sort_order: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
};

const EMPTY: Project = {
  id: 0,
  slug: "",
  sector: "semiconductor",
  stage: "early",
  name_zh: "",
  name_en: "",
  ticker: "",
  market: "",
  market_en: "",
  ipo_date: "",
  kexi_invest_date: "",
  exit_status: "hold",
  return_multiple: null,
  summary_zh: "",
  summary_en: "",
  body_zh_md: "",
  body_en_md: "",
  logo_key: "",
  cover_key: "",
  gallery_keys: [],
  timeline: [],
  sort_order: 0,
  active: true,
};

function dateOnly(v: string | null | undefined): string {
  if (!v) return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

/** 编辑页分区块 */
function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="pe-section">
      <div className="pe-section-head">
        <h3>{title}</h3>
        {hint && <span className="muted" style={{ fontSize: 12 }}>{hint}</span>}
      </div>
      {children}
    </section>
  );
}

/** 中英并排（同尺寸） */
function BiField({ label, cn, en, onCn, onEn, rows, required, placeholder }: {
  label: string; cn: string; en: string;
  onCn: (v: string) => void; onEn: (v: string) => void;
  rows?: number; required?: boolean; placeholder?: [string, string];
}) {
  const Control = rows ? "textarea" : "input";
  return (
    <div className="ce-field">
      <div className="ce-label">{label}{required ? " *" : ""}</div>
      <div className="ce-bilingual">
        <div className="ce-bi-col">
          <span className="ce-bi-tag">中</span>
          <Control className="ce-input" rows={rows} value={cn} required={required}
            placeholder={placeholder?.[0]}
            onChange={(e: any) => onCn(e.target.value)} />
        </div>
        <div className="ce-bi-mid">
          <TransButtons getCn={() => cn} getEn={() => en} setCn={onCn} setEn={onEn} title={label} />
        </div>
        <div className="ce-bi-col">
          <span className="ce-bi-tag">EN</span>
          <Control className="ce-input" rows={rows} value={en} required={required}
            placeholder={placeholder?.[1]}
            onChange={(e: any) => onEn(e.target.value)} />
        </div>
      </div>
    </div>
  );
}

/** 投资时间线结构化编辑 */
function TimelineEditor({ value, onChange }: { value: TimelineItem[]; onChange: (v: TimelineItem[]) => void }) {
  const upd = (i: number, patch: Partial<TimelineItem>) =>
    onChange(value.map((it, x) => (x === i ? { ...it, ...patch } : it)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const copy = value.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  return (
    <div className="tl-editor">
      {value.length > 0 && (
        <div className="tl-head">
          <span>年份</span><span>事件（中）</span><span /><span>事件（英）</span><span />
        </div>
      )}
      {value.map((it, i) => (
        <div className="tl-row" key={i}>
          <input className="tl-year" placeholder="2019" value={it.year}
            onChange={(e) => upd(i, { year: e.target.value })} />
          <input placeholder="科创板上市" value={it.event_zh ?? ""}
            onChange={(e) => upd(i, { event_zh: e.target.value })} />
          <TransButtons
            getCn={() => it.event_zh ?? ""} getEn={() => it.event_en ?? ""}
            setCn={(v) => upd(i, { event_zh: v })} setEn={(v) => upd(i, { event_en: v })} />
          <input placeholder="STAR Market IPO" value={it.event_en ?? ""}
            onChange={(e) => upd(i, { event_en: e.target.value })} />
          <span className="ce-array-tools">
            <button type="button" className="ce-tool" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button type="button" className="ce-tool" disabled={i === value.length - 1} onClick={() => move(i, 1)}>↓</button>
            <button type="button" className="ce-tool ce-tool-danger" onClick={() => onChange(value.filter((_, x) => x !== i))}>×</button>
          </span>
        </div>
      ))}
      <button type="button" className="ce-add" onClick={() => onChange([...value, { year: "", event_zh: "", event_en: "" }])}>
        + 添加节点
      </button>
    </div>
  );
}

export function ProjectsEditPage() {
  const { slug } = useParams<{ slug: string }>();
  const isNew = slug === "new";
  const nav = useNavigate();
  const [params] = useSearchParams();
  // 从「页面文案」投资组合页右键跳转而来：返回时回到页面文案并定位到原预览路由
  const fromPages = params.get("from") === "pages";
  const fromWebRoute = params.get("webroute");
  const goBack = () => {
    if (fromPages) {
      nav(fromWebRoute ? `/pages?webroute=${encodeURIComponent(fromWebRoute)}` : "/pages");
    } else {
      nav("/projects");
    }
  };

  const [form, setForm] = useState<Project>(EMPTY);
  const [gallery, setGallery] = useState<string[]>([]);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<null | "logo" | "cover">(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // 服务端当前的 active 值，用于判断保存时是否发生上线/下线变更
  const savedActive = useRef(true);
  const [confirmActive, setConfirmActive] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get<Project>(`/api/admin/projects/${slug}`)
      .then((p) => {
        setForm({
          ...p,
          ipo_date: dateOnly(p.ipo_date),
          kexi_invest_date: dateOnly(p.kexi_invest_date),
          ticker: p.ticker ?? "",
          market: p.market ?? "",
          market_en: p.market_en ?? "",
          exit_status: p.exit_status ?? "",
          body_zh_md: p.body_zh_md ?? "",
          body_en_md: p.body_en_md ?? "",
          logo_key: p.logo_key ?? "",
          cover_key: p.cover_key ?? "",
        });
        setGallery(Array.isArray(p.gallery_keys) ? p.gallery_keys : []);
        setTimeline(Array.isArray(p.timeline) ? p.timeline : []);
        savedActive.current = p.active;
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [slug, isNew]);

  const upd = <K extends keyof Project>(k: K, v: Project[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const uploadFor = async (which: "logo" | "cover", file: File) => {
    setUploading(which);
    setError(null);
    // 文件统一存 projects/<slug>/ 下；slug 未填则无法定位目录
    if (!form.slug.trim()) { setError("请先填写项目 slug，再上传图片"); setUploading(null); return; }
    try {
      const prefix = `projects/${form.slug.trim()}/`;
      const prev = which === "logo" ? form.logo_key : form.cover_key;
      const res = await api.upload<{ key: string; url: string }>("/api/admin/uploads", file, prefix);
      if (which === "logo") upd("logo_key", res.url);
      else upd("cover_key", res.url);
      api.dropFile(prev); // 换图：删旧图（全库复查，仍被已保存版本引用则保留）
    } catch (e) {
      if (e instanceof ApiError) setError(`上传失败：${JSON.stringify(e.body)}`);
      else setError(String(e));
    } finally {
      setUploading(null);
    }
  };

  // 状态（上线/下线）是否相对服务端发生了变更
  const activeChanged = !isNew && form.active !== savedActive.current;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 状态有变更时，先弹确认框，确认后再保存
    if (activeChanged) {
      setConfirmActive(true);
      return;
    }
    void doSave();
  };

  const doSave = async () => {
    setError(null);
    setSavedAt(null);

    const payload: Record<string, unknown> = {
      sector: form.sector,
      stage: form.stage,
      name_zh: form.name_zh,
      name_en: form.name_en,
      ticker: form.ticker || null,
      market: form.market || null,
      market_en: form.market_en || null,
      ipo_date: form.ipo_date || null,
      kexi_invest_date: form.kexi_invest_date || null,
      exit_status: form.exit_status || null,
      return_multiple: form.return_multiple === null || form.return_multiple === "" ? null : Number(form.return_multiple),
      summary_zh: form.summary_zh,
      summary_en: form.summary_en,
      body_zh_md: form.body_zh_md || null,
      body_en_md: form.body_en_md || null,
      logo_key: form.logo_key || null,
      cover_key: form.cover_key || null,
      gallery_keys: gallery,
      timeline: timeline.filter((t) => t.year || t.event_zh || t.event_en),
      sort_order: Number(form.sort_order) || 0,
      active: form.active,
    };
    if (isNew) payload.slug = form.slug;

    setSaving(true);
    try {
      if (isNew) {
        const created = await api.post<Project>("/api/admin/projects", payload);
        nav(`/projects/${created.slug}`, { replace: true });
      } else {
        await api.patch<Project>(`/api/admin/projects/${slug}`, payload);
        const fresh = await api.get<Project>(`/api/admin/projects/${slug}`);
        setForm((f) => ({ ...f, updated_at: fresh.updated_at, active: fresh.active }));
        savedActive.current = fresh.active;
        setSavedAt(new Date().toLocaleTimeString("zh-CN"));
      }
    } catch (err) {
      if (err instanceof ApiError) setError(`保存失败 (${err.status})：${JSON.stringify(err.body)}`);
      else setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const headerTitle = isNew ? "新建项目" : `编辑：${form.name_zh || form.slug}`;

  if (loading) return <div className="muted">加载中…</div>;

  const fileUrl = (key: string) =>
    /^https?:\/\//.test(key) || key.startsWith("/") ? key : `/api/files/${key}`;

  // logo / 主图上传区：预览图在上、操作按钮在下，整洁竖排
  const imgField = (which: "logo" | "cover") => {
    const field = which === "logo" ? "logo_key" : "cover_key";
    const key = (which === "logo" ? form.logo_key : form.cover_key) || "";
    const remove = () => { const u = key; upd(field, ""); api.dropFile(u); };
    return (
      <>
        {key && (
          <div className="pe-img-thumb">
            <img src={fileUrl(key)} alt="" onError={(e) => (e.currentTarget.style.opacity = "0.2")} />
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
          <label className="btn" style={{ margin: 0 }}>
            {uploading === which ? "上传中…" : (key ? "更换" : "上传…")}
            <input type="file" accept="image/*" style={{ display: "none" }} disabled={uploading !== null}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFor(which, f); e.target.value = ""; }} />
          </label>
          {key && (
            <button type="button" className="btn" style={{ color: "#b04a3a", borderColor: "#b04a3a" }}
              onClick={remove}>移除</button>
          )}
        </div>
      </>
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="page-header">
        <div className="page-title">{headerTitle}</div>
        <div className="page-actions" style={{ alignItems: "center", gap: 14 }}>
          {savedAt && <span className="success" style={{ fontSize: 12 }}>已保存 {savedAt}</span>}
          <button type="button" className="btn" onClick={goBack}>← {fromPages ? "返回页面文案" : "返回列表"}</button>
          <button type="submit" className="btn btn-primary" disabled={saving || uploading !== null}>
            {saving ? "保存中…" : (isNew ? "创建" : "保存")}
          </button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="pe-form">
        <Section title="基本信息">
          <div className="pe-grid-4">
            <div>
              <label>Slug *</label>
              <input value={form.slug} onChange={(e) => upd("slug", e.target.value)}
                disabled={!isNew} required placeholder="montage" />
            </div>
            <div>
              <label>赛道 *</label>
              <select value={form.sector} onChange={(e) => upd("sector", e.target.value)}>
                {SECTOR_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label>阶段 *</label>
              <select value={form.stage} onChange={(e) => upd("stage", e.target.value as Project["stage"])}>
                {STAGE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label>排序（大的在前）</label>
              <input type="number" value={form.sort_order} onChange={(e) => upd("sort_order", Number(e.target.value))} />
            </div>
          </div>
          <BiField label="公司名称" required
            cn={form.name_zh} en={form.name_en}
            onCn={(v) => upd("name_zh", v)} onEn={(v) => upd("name_en", v)}
            placeholder={["澜起科技", "Montage Technology"]} />
        </Section>

        <Section title="上市与退出" hint="未上市项目可留空">
          <div className="pe-grid-4">
            <div>
              <label>股票代码</label>
              <input value={form.ticker ?? ""} onChange={(e) => upd("ticker", e.target.value)} placeholder="688008" />
            </div>
            <div>
              <label>IPO 日期</label>
              <input type="date" value={form.ipo_date ?? ""} onChange={(e) => upd("ipo_date", e.target.value)} />
            </div>
            <div>
              <label>珂玺投资日期</label>
              <input type="date" value={form.kexi_invest_date ?? ""} onChange={(e) => upd("kexi_invest_date", e.target.value)} />
            </div>
            <div>
              <label>回报倍数</label>
              <input type="number" step="0.01" value={form.return_multiple ?? ""}
                onChange={(e) => upd("return_multiple", e.target.value as unknown as number)} placeholder="20" />
            </div>
          </div>
          {/* 交易所(中/英) + 退出状态 + 状态，三列。退出状态与状态各留一行占位，
             与交易所的「中/EN」标签行对齐 */}
          <div className="pe-exit-grid">
            <BiField label="交易所"
              cn={form.market ?? ""} en={form.market_en ?? ""}
              onCn={(v) => upd("market", v)} onEn={(v) => upd("market_en", v)}
              placeholder={["科创板", "STAR Market"]} />
            <div className="ce-field">
              <div className="ce-label">退出状态</div>
              <div className="ce-bi-col">
                <span className="ce-bi-tag" aria-hidden="true">&nbsp;</span>
                <select value={form.exit_status || "hold"} onChange={(e) => upd("exit_status", e.target.value)} style={{ width: "100%" }}>
                  {EXIT_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div className="ce-field">
              <div className="ce-label">状态</div>
              <div className="ce-bi-col">
                <span className="ce-bi-tag" aria-hidden="true">&nbsp;</span>
                <label className="ce-check" style={{ height: 36 }}>
                  <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} />
                  <span>{form.active ? "启用（公开可见）" : "已下线（不可见）"}</span>
                </label>
              </div>
            </div>
          </div>
        </Section>

        <Section title="公司简介">
          <BiField label="一句话简介" required rows={2}
            cn={form.summary_zh} en={form.summary_en}
            onCn={(v) => upd("summary_zh", v)} onEn={(v) => upd("summary_en", v)} />
          <BiField label="详情页正文（Markdown）" rows={12}
            cn={form.body_zh_md ?? ""} en={form.body_en_md ?? ""}
            onCn={(v) => upd("body_zh_md", v)} onEn={(v) => upd("body_en_md", v)} />
        </Section>

        <Section title="投资时间线">
          <TimelineEditor value={timeline} onChange={setTimeline} />
        </Section>

        <Section title="图片">
          <div className="pe-img-row">
            <div className="ce-field">
              <div className="ce-label">Logo</div>
              {imgField("logo")}
            </div>
            <div className="ce-field">
              <div className="ce-label">详情页主图</div>
              {imgField("cover")}
            </div>
          </div>
          <div className="ce-field">
            <div className="ce-label">图片画廊（点击 + 多选上传，←→ 调整顺序，× 删除）</div>
            <GalleryEditor value={gallery} onChange={setGallery} prefix={form.slug.trim() ? `projects/${form.slug.trim()}/` : ""} />
          </div>
        </Section>

        <div className="pe-footer">
          <button type="submit" className="btn btn-primary" disabled={saving || uploading !== null}>
            {saving ? "保存中…" : (isNew ? "创建" : "保存")}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            slug 创建后不可改{form.updated_at ? ` · 更新于 ${new Date(form.updated_at).toLocaleString("zh-CN")}` : ""}
          </span>
        </div>
      </div>

      {/* 上线 / 下线状态变更确认 */}
      <ConfirmDialog
        open={confirmActive}
        title={form.active ? "重新上线该项目？" : "下线该项目？"}
        message={
          form.active
            ? <>「{form.name_zh || form.slug}」将<strong>重新在公开站展示</strong>。确认保存？</>
            : <>「{form.name_zh || form.slug}」将<strong>从公开站下线</strong>，访客不可见。记录会保留，日后可再次上线恢复。确认保存？</>
        }
        confirmLabel={form.active ? "确认上线并保存" : "确认下线并保存"}
        danger={!form.active}
        onConfirm={() => { setConfirmActive(false); void doSave(); }}
        onCancel={() => setConfirmActive(false)}
      />
    </form>
  );
}
